/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import {
  type Campaign,
  type CustomerCampaign,
  type DiscountDefinition,
  fetchCustomerCampaignByLookupKey,
  isCoreSeatDiscount,
} from 'embercom/lib/billing/campaign';
import { BILLING_PERIODS } from 'embercom/lib/billing';
import moment from 'moment-timezone';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type CardlessConversionModalService from 'embercom/services/cardless-conversion-modal-service';
import type ReactTeammateAppIsland from 'embercom/services/react-teammate-app-island';

export const FREE_SEATS_CAMPAIGN_LOOKUP_KEY = 'campaign_free_seats_offer';

interface FreeSeatsCampaignMetadata {
  automationRate: number;
  finIsLive: boolean;
  subscriptionPhase: 'in_trial' | 'within_free_seats_window' | 'past_free_seats_window';
  daysRemainingInBillingCycle: number | null;
}

type CampaignLookupKey = 'campaign_lennys_podcast';
type CampaignTranslationKeys = {
  trialBannerText: string;
  trialBannerCtaLabel: string;
  checkoutsBannerText: string;
  successContentDescription: string;
  upgradeModalBannerText: string;
  welcomeScreenDescription: string;
  basketDescription: string;
  finMetricCardDescription: string;
};

export const CAMPAIGN_TRANSLATION_KEYS: Record<CampaignLookupKey, CampaignTranslationKeys> = {
  campaign_lennys_podcast: {
    trialBannerText: 'components.banners.campaigns.campaign_lennys_podcast-text',
    trialBannerCtaLabel: 'components.banners.campaigns.campaign_lennys_podcast-cta-label',
    checkoutsBannerText:
      'components.banners.campaigns.campaign_lennys_podcast-checkouts-banner-text',
    successContentDescription:
      'components.banners.campaigns.campaign_lennys_podcast-success-content-description',
    upgradeModalBannerText:
      'components.banners.campaigns.campaign_lennys_podcast-upgrade-modal-banner-text',
    welcomeScreenDescription:
      'components.banners.campaigns.campaign_lennys_podcast-welcome-screen-description',
    basketDescription:
      'signup.teams.pricing5.annual-plans.basket.lennys_podcast_campaign-basket-description',
    finMetricCardDescription:
      'signup.teams.pricing5.annual-plans.metrics.fin.lennys_podcast_campaign-description',
  },
};

export default class CompellingOfferCampaignService extends Service {
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare cardlessTrialService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare aiAgentSetupService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare cardlessConversionModalService: CardlessConversionModalService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIsland;

  @tracked campaign: Campaign | null = null;

  init() {
    super.init();
    this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'open-cardless-conversion-modal',
      () => {
        this.cardlessConversionModalService.toggle();
      },
    );
  }

  @keepLatestTask
  *fetchCampaign(): TaskGenerator<void> {
    if (this.currentCampaign?.lookup_key) {
      this.campaign = yield fetchCustomerCampaignByLookupKey(
        this.appService.app.id,
        this.currentCampaign.lookup_key,
      );
    }
  }

  get customer() {
    return this.customerService.customer;
  }

  get currentCampaign(): CustomerCampaign | undefined {
    return this.customer?.currentCampaign;
  }

  get redeemableCampaign(): CustomerCampaign | undefined {
    return this.customer?.redeemableCampaign;
  }

  get ongoingCampaign(): CustomerCampaign | undefined {
    return this.customer?.ongoingCampaign;
  }

  coreSeatDiscountForPlan(planId: string): DiscountDefinition | undefined {
    return this.campaign?.discount_definitions?.find(
      (dd) => dd.plan_id !== null && dd.plan_id.toString() === planId && isCoreSeatDiscount(dd),
    );
  }

  applyCoreSeatDiscount(planId: string, basePriceInDollars: number): number {
    let discount = this.coreSeatDiscountForPlan(planId);
    if (!discount) {
      return basePriceInDollars;
    }
    if (discount.discount_type === 'fixed') {
      let discountInDollars = Number((discount.discount_value / 100).toFixed(2));
      return Math.max(0, basePriceInDollars - discountInDollars);
    }
    return basePriceInDollars * (1 - discount.discount_value / 100);
  }

  get isFreeSeatsCampaign(): boolean {
    return (
      this.currentCampaign?.lookup_key === FREE_SEATS_CAMPAIGN_LOOKUP_KEY &&
      this.isEligibleForCampaignExperience
    );
  }

  get isEligibleForCampaignExperience(): boolean {
    return Boolean(this.customer) && !this.appService.app.isStandaloneApp;
  }

  private isExpired(date: Date | string | null | undefined): boolean {
    if (!date) {
      return false;
    }

    return new Date(date) < new Date();
  }

  private findTranslationKey(
    campaign: CustomerCampaign | null | undefined,
    key: keyof CampaignTranslationKeys,
  ): string | undefined {
    let lookupKey = campaign?.lookup_key;

    if (!lookupKey || !(lookupKey in CAMPAIGN_TRANSLATION_KEYS)) {
      return undefined;
    }

    return CAMPAIGN_TRANSLATION_KEYS[lookupKey as CampaignLookupKey][key];
  }

  private translateCampaignKey({
    campaign,
    key,
    params,
  }: {
    campaign?: CustomerCampaign;
    key: keyof CampaignTranslationKeys;
    params?: Record<string, unknown>;
  }): string {
    let translationKey = this.findTranslationKey(campaign, key);
    if (!translationKey) {
      return '';
    }

    return this.intl.t(translationKey, { htmlSafe: true, ...params }) as string;
  }

  get checkoutsBannerText(): string {
    return this.translateCampaignKey({
      key: 'checkoutsBannerText',
      campaign: this.redeemableCampaign,
    });
  }

  get trialBannerCtaLabel(): string {
    return this.translateCampaignKey({
      key: 'trialBannerCtaLabel',
      campaign: this.redeemableCampaign,
    });
  }

  get trialBannerText(): string {
    return this.translateCampaignKey({
      key: 'trialBannerText',
      campaign: this.redeemableCampaign,
      params: { trialEndInDays: this.cardlessTrialService.daysLeftOfTrial },
    });
  }

  successContentDescription(planName?: string): string {
    return String(
      this.translateCampaignKey({
        key: 'successContentDescription',
        params: { planName },
        campaign: this.ongoingCampaign,
      }),
    );
  }

  welcomeScreenDescription(planName: string): string {
    return this.translateCampaignKey({
      key: 'welcomeScreenDescription',
      params: { planName },
      campaign: this.redeemableCampaign,
    });
  }

  get upgradeModalBannerText(): string {
    let discountFinishesAt = this.ongoingCampaign?.discount_finishes_at;
    let hasExpiry = discountFinishesAt ? 'true' : 'false';
    let date = discountFinishesAt ? moment(discountFinishesAt).format('D MMM, YYYY') : '';
    return this.translateCampaignKey({
      key: 'upgradeModalBannerText',
      params: { hasExpiry, date },
      campaign: this.ongoingCampaign,
    });
  }

  campaignBasketDescription(billingPeriod: BILLING_PERIODS): string | undefined {
    if (billingPeriod === BILLING_PERIODS.Annual) {
      return undefined;
    }
    return this.translateCampaignKey({
      campaign: this.redeemableCampaign,
      key: 'basketDescription',
    });
  }

  get finMetricCardDescriptionKey(): string | undefined {
    return this.findTranslationKey(this.redeemableCampaign, 'finMetricCardDescription');
  }

  /*
    Returns metadata for the free seats campaign, including the current
    subscription phase:
    - in_trial: app is in trial or expired trial
    - within_free_seats_window: app is in their first billing cycle
    - past_free_seats_window: app is past their first billing cycle
  */
  get freeSeatsCampaignMetadata(): FreeSeatsCampaignMetadata {
    // TODO: Wire in real automation rate from campaign metadata
    let automationRate = 0.435;
    let finIsLive = !!this.aiAgentSetupService.isFinLive;

    if (this.cardlessTrialService.inTrialOrExpiredTrial) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'in_trial',
        daysRemainingInBillingCycle: null,
      };
    }

    let customer = this.customerService.customer;
    if (!customer) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'past_free_seats_window',
        daysRemainingInBillingCycle: null,
      };
    }

    let startedAt = customer.subscription?.startedAt;

    if (customer.hasActiveSubscription && startedAt) {
      let endDate = customer.currentBillingCycleEndDate;
      let daysRemainingInBillingCycle = endDate
        ? moment(endDate).diff(moment().startOf('day'), 'days')
        : null;

      let isInFirstBillingCycle =
        customer.currentBillingCycleStartDate &&
        moment(startedAt).format('YYYY-MM-DD') ===
          moment(customer.currentBillingCycleStartDate).format('YYYY-MM-DD');

      if (isInFirstBillingCycle) {
        return {
          automationRate,
          finIsLive,
          subscriptionPhase: 'within_free_seats_window',
          daysRemainingInBillingCycle,
        };
      }

      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'past_free_seats_window',
        daysRemainingInBillingCycle,
      };
    }

    return {
      automationRate,
      finIsLive,
      subscriptionPhase: 'past_free_seats_window',
      daysRemainingInBillingCycle: null,
    };
  }

  canViewGenericCampaignBanners(period: BILLING_PERIODS = BILLING_PERIODS.Monthly): boolean {
    return (
      this.isEligibleForCampaignExperience &&
      !!this.redeemableCampaign &&
      !this.isExpired(this.redeemableCampaign.redemption_expires_at) &&
      period === BILLING_PERIODS.Monthly &&
      !this.isFreeSeatsCampaign
    );
  }

  canViewRedeemedCampaignBanners(): boolean {
    return (
      this.isEligibleForCampaignExperience &&
      !!this.ongoingCampaign &&
      !this.isExpired(this.ongoingCampaign.discount_finishes_at)
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    compellingOfferCampaignService: CompellingOfferCampaignService;
    'compelling-offer-campaign-service': CompellingOfferCampaignService;
  }
}
